import {
  CircleStackIcon,
  GlobeAltIcon,
  LockClosedIcon,
} from '@heroicons/react/24/outline';
import { CloudIcon } from 'lucide-react';
import React, { ReactNode } from 'react';

import { useGetMonitoringCounts } from '@/hooks/useGetMonitoringStats';
import { useGetSubscription } from '@/hooks/useSubscription';
import { MonitoringCategories } from '@/types';
import { calculateAssetTotal } from '@/utils/monitoring';

import {
  CloudCard,
  MonitoringCategoryCard,
  SocialEngineeringCard,
  UsageDisplay,
} from './components';

const MONITORING_CATEGORY_ICON_MAP: Record<MonitoringCategories, ReactNode> = {
  cloud: (
    <CloudIcon className="text-chariot-stroke size-6 shrink-0 [&_path]:stroke-[2px]" />
  ),
  externalNetwork: (
    <GlobeAltIcon className="text-chariot-stroke size-6 shrink-0" />
  ),
  code: <CircleStackIcon className="text-chariot-stroke size-6 shrink-0" />,
  internalNetwork: (
    <LockClosedIcon className="text-chariot-stroke size-6 shrink-0" />
  ),
  applications: (
    <GlobeAltIcon className="text-chariot-stroke size-6 shrink-0" />
  ),
};

/**
 * Monitoring component handles its own data fetching to work with Suspense pattern.
 * Fetches both subscription data (for limits) and monitoring counts internally.
 */
export function Monitoring(props: { email?: string; hideLimits?: boolean }) {
  // Fetch monitoring counts and subscription data
  const { data: monitoringCounts, status } = useGetMonitoringCounts({
    email: props.email,
  });
  const { data: subscriptionValue } = useGetSubscription({
    email: props.email,
  });

  // React 19: Simple reduce operations don't need useMemo (must be before early return for hooks rules)
  const totalAssets =
    !monitoringCounts || !subscriptionValue?.attackSurface
      ? 0
      : calculateAssetTotal(monitoringCounts, subscriptionValue.attackSurface);

  // React 19: Simple reduce operations don't need useMemo
  const totalApplications = !monitoringCounts?.applications
    ? 0
    : Object.values(monitoringCounts.applications).reduce(
        (acc, curr) => acc + curr,
        0
      );

  // Return null while loading to let parent Suspense handle loading state
  const isLoading = status === 'pending';
  if (isLoading || !monitoringCounts) {
    return null;
  }

  const { applications } = monitoringCounts;

  // Calculate current applications monitored (webseed count)
  const currentApplicationsMonitored = applications.webseed ?? 0;

  // Get limits from subscription data
  const maxAssetLimit = subscriptionValue?.numberOfAssets ?? 0;
  const maxApplicationLimit =
    subscriptionValue?.numberOfApplications ?? totalApplications ?? 0;

  return (
    <div className="flex flex-col gap-6">
      {!props.hideLimits && (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <UsageDisplay
            label="Scannable Asset Current Coverage vs Asset Contract Limit"
            current={totalAssets}
            limit={maxAssetLimit}
            showTooltip
            tooltipText="Number of assets currently being monitored compared to your subscription's contracted limit"
          />
          <UsageDisplay
            label="Application Current Coverage vs Application Contract Limit"
            current={currentApplicationsMonitored}
            limit={maxApplicationLimit}
            showTooltip
            tooltipText="Number of applications currently being monitored compared to your subscription's contracted limit"
          />
        </div>
      )}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3 xl:grid-cols-3">
        {/* Row 1: Internet Perimeter, Applications, Social Engineering */}
        {/* Row 2: Internal Intranet, Cloud, Code */}

        {(
          [
            'externalNetwork',
            'applications',
            'internalNetwork',
            'code',
          ] as MonitoringCategories[]
        ).map(categoryKey => {
          const category = monitoringCounts[categoryKey];

          return (
            <React.Fragment key={categoryKey}>
              <MonitoringCategoryCard
                categoryKey={categoryKey}
                categoryIcon={MONITORING_CATEGORY_ICON_MAP[categoryKey]}
                category={
                  category as Record<string, number | Record<string, number>>
                }
                monitoringCounts={monitoringCounts}
                subscriptionValue={subscriptionValue}
                isLoading={isLoading}
              />

              {/* Social Engineering follows Applications (position 3 in grid) */}
              {categoryKey === 'applications' && (
                <SocialEngineeringCard
                  monitoringCounts={monitoringCounts}
                  subscriptionValue={subscriptionValue}
                  isLoading={isLoading}
                />
              )}

              {/* Cloud follows internalNetwork (position 5 in grid) */}
              {categoryKey === 'internalNetwork' && (
                <CloudCard
                  monitoringCounts={monitoringCounts}
                  subscriptionValue={subscriptionValue}
                  isLoading={isLoading}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
